import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/i18n";

const LAB_IMG = "https://images.pexels.com/photos/10638065/pexels-photo-10638065.jpeg";

const statValues = ["06", "02", "01"];

export default function Lab() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [-50, 50]);
  const statLabels = t("lab.stats");

  return (
    <section
      id="lab"
      ref={ref}
      data-testid="lab-section"
      className="relative overflow-hidden border-y border-[#E6E2DC] bg-surface py-24 md:py-36"
    >
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 px-6 md:grid-cols-2 md:px-12">
        <div className="relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.33, 1, 0.68, 1] }}
            className="relative overflow-hidden"
          >
            <motion.img
              style={{ y: yImg }}
              src={LAB_IMG}
              alt="Students learning in a computer lab"
              data-testid="lab-image"
              className="h-[420px] w-full scale-110 object-cover md:h-[560px]"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-neon-teal/20 via-transparent to-neon-pink/10 mix-blend-overlay" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          </motion.div>
          <div
            className="absolute -left-4 -top-4 h-24 w-24 border-l-2 border-t-2 border-neon-teal"
            aria-hidden="true"
          />
          <div
            className="absolute -bottom-4 -right-4 h-24 w-24 border-b-2 border-r-2 border-neon-pink"
            aria-hidden="true"
          />
        </div>

        <div className="flex flex-col justify-center">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-neon-teal"
            data-testid="lab-eyebrow"
          >
            {t("lab.eyebrow")}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-4xl font-medium tracking-tight text-[#141210] md:text-6xl"
            data-testid="lab-heading"
          >
            {t("lab.headingA")} <span className="text-neon-pink">{t("lab.headingB")}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-[#6B655F] md:text-lg"
            data-testid="lab-body"
          >
            {t("lab.body")}
          </motion.p>

          <div className="mt-12 grid grid-cols-3 gap-6" data-testid="lab-stats">
            {statValues.map((value, i) => (
              <motion.div
                key={value}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.25 + i * 0.1 }}
                className="border-l border-[#DCD6CE] pl-4"
              >
                <div className="font-display text-4xl font-semibold text-neon-green md:text-5xl">
                  {value}
                </div>
                <div className="mt-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#6B655F]">
                  {statLabels[i]}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
